import { useState } from "react";
import { Link } from "react-router-dom";
import {
    Sparkles, PlayCircle, CheckCircle2, ArrowRight, Repeat,
    LayoutDashboard, FolderKanban, Wrench, Wallet, ShoppingCart,
    FileBarChart2, Bot, Database, BookOpen, Bell, LineChart, Boxes, Rocket,
} from "lucide-react";
import { EDI } from "@/constants/testIds";

const TRIGGER = {
    title: "Engineer updates the Rip Tank data sheet",
    subtitle: "SGSB-KB-DS-004120 · Harmony Gold · Rev C → Rev D",
    fields: [
        { name: "Diameter", from: "4.2 m", to: "4.6 m" },
        { name: "Working Volume", from: "62 m³", to: "74 m³" },
        { name: "Material", from: "SS304", to: "SS316L" },
        { name: "Vendor History", from: "3 refs", to: "5 refs" },
    ],
};

const DOWNSTREAM = [
    { name: "Executive Dashboard",          icon: LayoutDashboard, tag: "Portfolio KPI refreshed",             detail: "Reuse % and hours saved recomputed" },
    { name: "Project Dashboard",            icon: FolderKanban,    tag: "Progress metric updated",             detail: "Kolomela Concentrator progress synced" },
    { name: "Engineering Dashboard",        icon: Wrench,          tag: "Discipline coverage updated",         detail: "Mechanical discipline metrics refreshed" },
    { name: "Procurement Dashboard",        icon: ShoppingCart,    tag: "Long-lead item flagged",              detail: "Vendor history expanded to 5 refs" },
    { name: "Commercial Dashboard",         icon: Wallet,          tag: "Cost baseline updated",               detail: "Material change propagated to variance" },
    { name: "Weekly Executive Report",      icon: FileBarChart2,   tag: "Section regenerated",                 detail: "Engineering progress paragraph refreshed" },
    { name: "Executive AI (GPT-5.2)",       icon: Bot,             tag: "Context re-grounded",                 detail: "New spec available to executive queries" },
    { name: "Knowledge Repository",         icon: Database,        tag: "Asset re-classified",                 detail: "Auto-tagged: Gold · Mechanical · Rip Tank" },
    { name: "Lessons Learned",              icon: BookOpen,        tag: "Cross-referenced",                    detail: "SS316L trigger linked to LL-000191" },
    { name: "Business Value Dashboard",     icon: LineChart,       tag: "Hours saved incremented",             detail: "+10.2 engineering hours captured" },
    { name: "Portfolio KPIs",               icon: Boxes,           tag: "Aggregate KPIs recomputed",           detail: "Portfolio Reuse % ticked up" },
    { name: "Alerts",                       icon: Bell,            tag: "Retirement risk alert",               detail: "Vendor concentration risk within tolerance" },
    { name: "Trend Analytics",              icon: LineChart,       tag: "Trend recalculated",                  detail: "Rip Tank material trend refreshed" },
];

export default function CaptureOnce() {
    const [triggered, setTriggered] = useState(false);
    const [completed, setCompleted] = useState(false);

    const trigger = () => {
        if (triggered) return;
        setTriggered(true);
        // Reveal all surfaces staggered — mark as completed when the last one lands
        const total = DOWNSTREAM.length * 120 + 800;
        setTimeout(() => setCompleted(true), total);
    };

    const reset = () => {
        setCompleted(false);
        setTriggered(false);
    };

    return (
        <div className="space-y-8" data-testid={EDI.captureOnceRoot}>
            <div className="relative overflow-hidden rounded-2xl glass p-8">
                <div
                    className="absolute inset-0 opacity-40 pointer-events-none"
                    style={{
                        background:
                            "radial-gradient(ellipse at 90% 10%, rgba(212,175,55,0.24), transparent 55%), radial-gradient(ellipse at 10% 90%, rgba(59,130,246,0.20), transparent 55%)",
                    }}
                />
                <div className="relative">
                    <div className="kicker text-[var(--sgsb-gold)]">Enterprise Delivery Intelligence™ · Executive Demonstration</div>
                    <h1 className="mt-2 font-display font-black text-white text-4xl md:text-5xl tracking-tighter leading-[0.98]">
                        Capture Once.<br />
                        <span className="text-[var(--sgsb-gold)]">Use Many Times.</span>
                    </h1>
                    <p className="mt-4 max-w-3xl text-slate-300 font-body text-base leading-relaxed">
                        Watch a single engineering update propagate across the SGS Bateman enterprise in
                        under three seconds. One data sheet edit — thirteen downstream surfaces refreshed —
                        zero duplicate reporting.
                    </p>
                </div>
            </div>

            {/* Step 1 — Trigger */}
            <div className="glass rounded-xl p-6">
                <div className="flex items-center gap-3">
                    <div className="h-8 w-8 rounded-full bg-[var(--sgsb-gold)] text-slate-950 grid place-items-center font-display font-black text-sm">1</div>
                    <div className="kicker text-slate-400">Trigger</div>
                    <div className="flex-1 h-px bg-slate-800/70" />
                </div>

                <div className="mt-4 grid grid-cols-1 lg:grid-cols-[1.4fr_auto_1fr] gap-6 items-center">
                    <div>
                        <div className="kicker text-[var(--sgsb-gold)] text-[10px]">Engineering update</div>
                        <h3 className="mt-1 font-display font-black text-white text-xl tracking-tight">{TRIGGER.title}</h3>
                        <div className="mt-1 text-xs text-slate-500 font-body">{TRIGGER.subtitle}</div>

                        <div className="mt-4 rounded-md bg-slate-950/60 border border-slate-800 divide-y divide-slate-800">
                            {TRIGGER.fields.map((f) => (
                                <div key={f.name} className="flex items-center px-3 py-2 text-sm font-body">
                                    <span className="w-40 text-slate-500 text-xs uppercase tracking-wider">{f.name}</span>
                                    <span className="text-slate-500 line-through">{f.from}</span>
                                    <ArrowRight className="mx-2 h-3.5 w-3.5 text-slate-600" strokeWidth={2} />
                                    <span className="text-[var(--sgsb-gold)] font-display font-bold">{f.to}</span>
                                </div>
                            ))}
                        </div>
                    </div>

                    <div className="hidden lg:flex items-center justify-center">
                        <ArrowRight className="h-6 w-6 text-slate-500" strokeWidth={1.5} />
                    </div>

                    <div className="flex flex-col gap-3">
                        <button
                            data-testid={EDI.captureOnceTrigger}
                            onClick={trigger}
                            disabled={triggered}
                            className="inline-flex items-center justify-center gap-2 rounded-md bg-[var(--sgsb-gold)] text-slate-950 px-5 py-3 font-display font-bold text-sm hover:bg-[color:var(--sgsb-gold-soft)] disabled:opacity-60 transition-colors duration-200"
                        >
                            <PlayCircle className="h-4 w-4" strokeWidth={2} />
                            {triggered ? (completed ? "Propagation complete" : "Propagating…") : "Save & propagate"}
                        </button>
                        <button
                            data-testid={EDI.captureOnceReset}
                            onClick={reset}
                            disabled={!triggered}
                            className="inline-flex items-center justify-center gap-2 rounded-md border border-slate-700 text-slate-300 px-5 py-2 font-display font-bold text-xs hover:border-[color:var(--sgsb-gold)]/50 hover:text-[var(--sgsb-gold)] disabled:opacity-40 transition-colors duration-200"
                        >
                            <Repeat className="h-3.5 w-3.5" strokeWidth={2} />
                            Reset demonstration
                        </button>
                    </div>
                </div>
            </div>

            {/* Step 2 — Ripple */}
            <div className="glass rounded-xl p-6">
                <div className="flex items-center gap-3">
                    <div className="h-8 w-8 rounded-full bg-[var(--sgsb-gold)] text-slate-950 grid place-items-center font-display font-black text-sm">2</div>
                    <div className="kicker text-slate-400">Automatic propagation across the enterprise</div>
                    <div className="flex-1 h-px bg-slate-800/70" />
                    <span className="kicker text-[10px] text-slate-500">{DOWNSTREAM.length} downstream surfaces</span>
                </div>

                <div className="mt-5 grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
                    {DOWNSTREAM.map((s, i) => {
                        const Icon = s.icon;
                        return (
                            <div
                                key={s.name}
                                data-testid={EDI.captureOnceSurface(i)}
                                className="rounded-md border p-4 flex items-start gap-3 relative overflow-hidden"
                                style={{
                                    borderColor: triggered ? "rgba(212,175,55,0.55)" : "rgba(148,163,184,0.14)",
                                    background: triggered ? "rgba(212,175,55,0.06)" : "rgba(2,6,23,0.55)",
                                    opacity: triggered ? 1 : 0.55,
                                    transform: triggered ? "translateY(0)" : "translateY(6px)",
                                    transition: "opacity 400ms ease, transform 400ms ease, background-color 400ms ease, border-color 400ms ease",
                                    transitionDelay: `${i * 120}ms`,
                                }}
                            >
                                <div
                                    className="h-9 w-9 rounded-md grid place-items-center shrink-0"
                                    style={{
                                        borderWidth: 1,
                                        borderStyle: "solid",
                                        borderColor: triggered ? "rgba(212,175,55,0.6)" : "rgba(148,163,184,0.25)",
                                        background: triggered ? "rgba(212,175,55,0.12)" : "rgba(2,6,23,0.6)",
                                    }}
                                >
                                    <Icon
                                        className="h-4 w-4"
                                        strokeWidth={1.5}
                                        style={{ color: triggered ? "#D4AF37" : "#94A3B8" }}
                                    />
                                </div>
                                <div className="min-w-0 flex-1">
                                    <div className="flex items-center gap-2">
                                        <div className="font-display font-bold text-white text-sm tracking-tight truncate">{s.name}</div>
                                        {triggered && (
                                            <CheckCircle2
                                                className="h-3.5 w-3.5 text-[var(--sgsb-gold)] shrink-0"
                                                strokeWidth={2}
                                                style={{
                                                    opacity: 0,
                                                    animation: triggered ? "sgsb-fade-in 400ms ease forwards" : "none",
                                                    animationDelay: `${i * 120 + 300}ms`,
                                                }}
                                            />
                                        )}
                                    </div>
                                    <div className="text-[11px] text-[var(--sgsb-gold)] kicker mt-0.5">{s.tag}</div>
                                    <div className="text-[11px] text-slate-400 font-body mt-1 leading-relaxed">{s.detail}</div>
                                </div>
                            </div>
                        );
                    })}
                </div>
            </div>

            {/* Step 3 — Impact */}
            <div className="glass rounded-xl p-6 relative overflow-hidden">
                <div
                    className="absolute inset-0 opacity-40 pointer-events-none"
                    style={{
                        background:
                            "radial-gradient(ellipse at 90% 10%, rgba(16,185,129,0.20), transparent 55%), radial-gradient(ellipse at 10% 90%, rgba(212,175,55,0.16), transparent 55%)",
                    }}
                />
                <div className="relative">
                    <div className="flex items-center gap-3">
                        <div className="h-8 w-8 rounded-full bg-[var(--sgsb-gold)] text-slate-950 grid place-items-center font-display font-black text-sm">3</div>
                        <div className="kicker text-slate-400">Enterprise impact</div>
                        <div className="flex-1 h-px bg-slate-800/70" />
                    </div>

                    <div className="mt-5 grid grid-cols-2 md:grid-cols-4 gap-4">
                        {[
                            ["Update Made", "1 data sheet"],
                            ["Surfaces Refreshed", "13"],
                            ["Duplicate Reporting", "0"],
                            ["Total Propagation", completed ? "< 3 s" : (triggered ? "in flight…" : "not started")],
                        ].map(([k, v]) => (
                            <div key={k} className="rounded-md bg-slate-950/40 border border-slate-800 p-3">
                                <div className="kicker text-slate-500 text-[10px]">{k}</div>
                                <div className="mt-1 font-display font-black text-white text-xl tracking-tight">{v}</div>
                            </div>
                        ))}
                    </div>

                    <div className="mt-5 flex flex-wrap items-center gap-3">
                        <div className="text-xs text-slate-400 font-body">
                            {completed
                                ? "One update → enterprise-wide intelligence. Every dashboard, report, alert and AI answer is now current."
                                : (triggered
                                    ? "Propagation in flight — every downstream surface will refresh automatically."
                                    : "Click ‘Save & propagate’ above to watch the enterprise refresh in under three seconds.")}
                        </div>
                        <div className="flex-1" />
                        <Link
                            to="/edi/business-value"
                            className="inline-flex items-center gap-2 rounded-md border border-[color:var(--sgsb-gold)]/40 text-[var(--sgsb-gold)] px-3 py-1.5 font-display font-bold text-xs hover:bg-[color:var(--sgsb-gold)]/10 transition-colors duration-200"
                        >
                            <Rocket className="h-3.5 w-3.5" strokeWidth={2} />
                            See the Business Value
                        </Link>
                    </div>
                </div>
            </div>

            {/* Scoped keyframes (isolated to this page) */}
            <style>{`
                @keyframes sgsb-fade-in {
                    from { opacity: 0; transform: scale(0.8); }
                    to   { opacity: 1; transform: scale(1); }
                }
            `}</style>
        </div>
    );
}
